import { cleanUpStructures } from '@peruselab/structure-editor/chemistry/structureCleanup';
import {
  collapseHydrogens,
  expandHydrogens,
  hasFoldableHydrogens
} from '@peruselab/structure-editor/domain/explicitHydrogens';
import { alignableObjectsInSelection } from '@peruselab/structure-editor/domain/objectAlignment';
import { functionalGroupDocument } from '@peruselab/structure-editor/chemistry/functionalGroups';
import { placeFunctionalGroupAt } from '@peruselab/structure-editor/domain/functionalGroupPlacement';
import {
  contractedAttachments,
  hangingAtoms,
  withContractedGroups
} from '@peruselab/structure-editor/domain/contractedGroups';
import {
  contractRecognizedGroups,
  expandContractedGroups
} from '@peruselab/structure-editor/chemistry/groupContraction';
import { calloutSelection, setCalloutAttachment } from '@peruselab/structure-editor/domain/callouts';
import { rotateStructures3d, type RotationAxis } from '@peruselab/structure-editor/domain/rotation3d';
import { useCallback, useMemo, useRef, useState } from 'react';
import { unprojectPointPreservingAspect } from '@peruselab/structure-editor/domain/aspectProjection';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import {
  emptySelection,
  hasSelection,
  type ArmedBondTool,
  type EditorSelection
} from '@peruselab/structure-editor/domain/editor';
import {
  addAtom,
  createEmptyDrawingDocument,
  cycleBondDoubleSide,
  deleteAtom,
  deleteBond,
  moveAtoms,
  swapWedgesFrom,
  reverseBondDative,
  reverseBondWavy,
  reverseBondWedge,
  setAtomAtomicNumber,
  setAtomFormalCharge,
  setAtomIsotope,
  setAtomRadicalElectronCount,
  setAtomResonanceCharge,
  setBondCrossedDouble,
  setBondDative,
  setBondAgostic,
  setBondHydrogen,
  ionizeBond,
  setBondOrder,
  setBondWavy,
  setBondWedge,
  setImplicitHydrogenDirection
} from '@peruselab/structure-editor/domain/graphDrawing';
import {
  containPastedAtoms,
  copyFragment,
  pasteFragment,
  placeAlongside,
  type ClipboardFragment
} from '@peruselab/structure-editor/domain/clipboard';
import { pasteTexts, textOnlySelection } from '@peruselab/structure-editor/domain/textClipboard';
import {
  alignObjects,
  reorderAnnotations,
  translateAnnotations,
  type AlignKind,
  type StackingCommand
} from '@peruselab/structure-editor/domain/objectAlignment';
import { pageOf } from '@peruselab/structure-editor/domain/page';
import { pageRect } from '@peruselab/structure-editor/domain/pageExtent';
import type { BondStereoValue } from '@peruselab/structure-editor/domain/documentSelectors';
import type { DrawingAtom } from '@peruselab/structure-editor/domain/graphDrawing';
import type { NormalizedPoint } from '@peruselab/structure-editor/model';
import type { DrawingDocument, PageSetup, TextAnnotation } from '../generated/drawing-contract';
import type {
  ReactionArrowAnnotationKind as ReactionArrowKind,
  ReactionArrowOverlay
} from '@peruselab/structure-editor/chemistry/reactionParticipant';

/** Room (drawing units, about one bond) kept between an added structure and the drawing's edge. */
const ADD_TO_PAPER_MARGIN = 30;

/** #700 -- where an added structure goes: centred at a paper-normalized point (a drop), or at a
 *  point (document units) chosen from its size (a reaction box); without one, beside the drawing. */
export type AdditionTarget =
  | { readonly paper: NormalizedPoint }
  | { readonly place: (extent: { readonly width: number; readonly height: number }) => { x: number; y: number } };

export interface UseDrawingCommandsOptions {
  readonly onCopySelection?: (() => void) | undefined;
  readonly document: DrawingDocument;
  readonly selection: EditorSelection;
  readonly setSelection: (next: EditorSelection) => void;
  readonly commitDocument: (change: (current: DrawingDocument) => DrawingDocument) => void;
  readonly setIsOversizedImport: (next: boolean) => void;
  readonly paperSize: PaperSize;
  readonly isOversizedImport: boolean;
  readonly atoms: readonly DrawingAtom[];
  readonly selectedTransformAtomIds: readonly string[];
  // #577 -- Center of Rotation's custom pivot (normalized, same space as DrawingAtom.position).
  // Null means "use the selection's centroid," transformSelection's original rotate-left/right
  // behavior.
  readonly rotationPivot: NormalizedPoint | null;
  readonly armedBondTool: ArmedBondTool;
  readonly singleAtomId: string | undefined;
  readonly stereoBonds: Readonly<Record<string, BondStereoValue>>;
  /** #1151 -- Align/Distribute with the reaction and its boxes arranged as such (useReactionBoxes). */
  readonly alignInReaction?: typeof alignObjects | undefined;
}

/**
 * Atom/bond mutation commands for useDrawingDocument.ts (roadmap item 35): direct user edits to
 * an existing selection or active bond, plus deleteSelection/clearDrawing (which also touch
 * selection state, not just the document). Every command here goes through `commitDocument`
 * (owned by useDocumentHistory) rather than a raw history setter.
 */
export function useDrawingCommands({
  onCopySelection,
  document,
  selection,
  setSelection,
  commitDocument,
  setIsOversizedImport,
  paperSize,
  isOversizedImport,
  atoms,
  selectedTransformAtomIds,
  rotationPivot,
  armedBondTool,
  singleAtomId,
  stereoBonds,
  alignInReaction = alignObjects
}: UseDrawingCommandsOptions) {
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  const cleanupRunning = useRef(false);
  const cleanUpSelection = useCallback(async () => {
    if (cleanupRunning.current) return;
    cleanupRunning.current = true;
    setIsCleaningUp(true);
    setCleanupError(null);
    const snapshot = document;
    try {
      const cleaned = await cleanUpStructures(snapshot, selection);
      // A delayed WASM load must not overwrite edits made while it was loading.
      commitDocument((current) => (current === snapshot ? cleaned : current));
    } catch (error) {
      setCleanupError(error instanceof Error ? error.message : 'Could not clean up the selected structure.');
    } finally {
      cleanupRunning.current = false;
      setIsCleaningUp(false);
    }
  }, [document, selection, commitDocument]);

  // #736 -- a functional group's atoms on a clicked atom, or alone where the paper was clicked. The
  // group is laid out once (RDKit), then placed in one Undo step.
  const [groupError, setGroupError] = useState<string | null>(null);
  const placeFunctionalGroup = useCallback(
    async (key: string, target: { readonly atomId: string } | { readonly point: NormalizedPoint }) => {
      setGroupError(null);
      try {
        const group = await functionalGroupDocument(key);
        // #736 step 2 (owner): a placed group is drawn as its label at once.
        commitDocument((current) => {
          const placed = placeFunctionalGroupAt(current, group, target);
          const hanging = placed.attachmentId
            ? hangingAtoms(placed.document, placed.attachmentId, placed.restId)
            : null;
          return placed.attachmentId && hanging?.size
            ? withContractedGroups(placed.document, [
                { attachmentId: placed.attachmentId, group: key, atomIds: [...hanging] }
              ])
            : placed.document;
        });
      } catch (error) {
        setGroupError(error instanceof Error ? error.message : 'Could not place the group.');
      }
    },
    [commitDocument]
  );

  // #777 -- hydrogens drawn as atoms, or folded back: in the selected molecules, or the whole
  // drawing when nothing is selected. One Undo step each.
  const hydrogenScope = useCallback(() => {
    const molecules = alignableObjectsInSelection(document, selection).filter((object) => object.kind === 'molecule');
    return molecules.length ? new Set(molecules.flatMap((molecule) => molecule.atomIds)) : undefined;
  }, [document, selection]);
  const showHydrogens = useCallback(() => {
    const scope = hydrogenScope();
    commitDocument((current) => expandHydrogens(current, scope));
  }, [commitDocument, hydrogenScope]);
  const hideHydrogens = useCallback(() => {
    const scope = hydrogenScope();
    commitDocument((current) => collapseHydrogens(current, scope));
  }, [commitDocument, hydrogenScope]);
  // #736 step 2: Tools' "Show Groups as Labels" -- ticked while a group in scope is drawn as its
  // label; ticking contracts every recognised group in scope, unticking draws their atoms again.
  const groupsContracted = useMemo(
    () => contractedAttachments(document, hydrogenScope()).length > 0,
    [document, hydrogenScope]
  );
  const setGroupsContracted = useCallback(
    async (contracted: boolean) => {
      const scope = hydrogenScope();
      const snapshot = document;
      try {
        const next = contracted
          ? await contractRecognizedGroups(snapshot, scope)
          : await expandContractedGroups(snapshot, scope);
        // A slow RDKit load must not overwrite edits made meanwhile.
        commitDocument((current) => (current === snapshot ? next : current));
      } catch (error) {
        setGroupError(error instanceof Error ? error.message : 'Could not change the groups.');
      }
    },
    [commitDocument, document, hydrogenScope]
  );

  // #777 (owner): Tools' "Show Hydrogens" checkbox -- ticked while hydrogens it could fold are drawn.
  const hydrogensShown = useMemo(() => hasFoldableHydrogens(document, hydrogenScope()), [document, hydrogenScope]);
  const setHydrogensShown = useCallback(
    (shown: boolean) => (shown ? showHydrogens() : hideHydrogens()),
    [showHydrogens, hideHydrogens]
  );

  const updateImplicitHydrogenDirection = useCallback(
    (atomId: string, direction: Readonly<{ x: number; y: number }>) => {
      commitDocument((current) => setImplicitHydrogenDirection(current, atomId, direction));
    },
    [commitDocument]
  );

  const moveSelectedAtoms = useCallback(
    (
      positions: Readonly<Record<string, Readonly<{ x: number; y: number }>>>,
      // #1047 -- a further change in the same Undo step (a flip's wedge swap).
      andThen: (document: DrawingDocument) => DrawingDocument = (document) => document
    ) => {
      commitDocument((current) =>
        andThen(
          moveAtoms(
            current,
            Object.fromEntries(
              Object.entries(positions).map(([id, position]) => [
                id,
                unprojectPointPreservingAspect(
                  position,
                  { width: current.depiction.coordinateSet.width, height: current.depiction.coordinateSet.height },
                  paperSize,
                  !isOversizedImport
                )
              ])
            )
          )
        )
      );
    },
    [commitDocument, isOversizedImport, paperSize]
  );

  // #637 -- a paper-normalized offset as a coordinate-normalized one (the projection is affine, so
  // the difference of two unprojected points is the offset; unclamped, so offsets near the edge
  // stay exact).
  const toCoordinateOffset = useCallback(
    (current: DrawingDocument, from: NormalizedPoint, to: NormalizedPoint) => {
      const size = { width: current.depiction.coordinateSet.width, height: current.depiction.coordinateSet.height };
      const a = unprojectPointPreservingAspect(from, size, paperSize, !isOversizedImport, false);
      const b = unprojectPointPreservingAspect(to, size, paperSize, !isOversizedImport, false);
      return { x: b.x - a.x, y: b.y - a.y };
    },
    [isOversizedImport, paperSize]
  );

  // #637 -- a dragged selection moves as a whole: the atoms go to their dragged positions and every
  // selected annotation moves by the same offset, in one history step.
  const moveSelection = useCallback(
    (positions: Readonly<Record<string, Readonly<{ x: number; y: number }>>>) => {
      const annotationIds = new Set(selection.annotationIds ?? []);
      const atom = atoms.find((candidate) => positions[candidate.id]);
      commitDocument((current) => {
        const moved = moveAtoms(
          current,
          Object.fromEntries(
            Object.entries(positions).map(([id, position]) => [
              id,
              unprojectPointPreservingAspect(
                position,
                { width: current.depiction.coordinateSet.width, height: current.depiction.coordinateSet.height },
                paperSize,
                !isOversizedImport
              )
            ])
          )
        );
        if (!atom || annotationIds.size === 0) return moved;
        const offset = toCoordinateOffset(current, atom.position, positions[atom.id]!);
        const { width, height } = current.depiction.coordinateSet;
        return translateAnnotations(moved, annotationIds, offset.x * width, offset.y * height);
      });
    },
    [atoms, commitDocument, isOversizedImport, paperSize, selection.annotationIds, toCoordinateOffset]
  );

  // #637 -- the selected annotations dragged without structure (by a text block), one step.
  const moveSelectedAnnotations = useCallback(
    (offset: NormalizedPoint) => {
      const annotationIds = new Set(selection.annotationIds ?? []);
      commitDocument((current) => {
        const delta = toCoordinateOffset(current, { x: 0, y: 0 }, offset);
        const { width, height } = current.depiction.coordinateSet;
        return translateAnnotations(current, annotationIds, delta.x * width, delta.y * height);
      });
    },
    [commitDocument, selection.annotationIds, toCoordinateOffset]
  );

  const transformSelection = useCallback(
    (kind: 'left' | 'right' | 'flipHorizontal' | 'flipVertical') => {
      const selected = atoms.filter((atom) => selectedTransformAtomIds.includes(atom.id));
      if (selected.length === 0) return;
      const points = selected.map((atom) => ({
        id: atom.id,
        x: atom.position.x * paperSize.width,
        y: atom.position.y * paperSize.height
      }));
      const cx = points.reduce((sum, point) => sum + point.x, 0) / points.length;
      const cy = points.reduce((sum, point) => sum + point.y, 0) / points.length;
      // #577 -- rotate-left/right pivot around the custom Center of Rotation point when one is
      // set, instead of the selection's centroid. Flip keeps mirroring through the centroid --
      // the custom pivot is a rotate-only concept, per the confirmed interaction design.
      const pivot =
        rotationPivot && (kind === 'left' || kind === 'right')
          ? { x: rotationPivot.x * paperSize.width, y: rotationPivot.y * paperSize.height }
          : { x: cx, y: cy };
      const transformedPoints = points.map((point) => {
        const dx = point.x - cx;
        const dy = point.y - cy;
        const dxPivot = point.x - pivot.x;
        const dyPivot = point.y - pivot.y;
        // flipHorizontal mirrors across the vertical axis through the centroid (x negates, y is
        // unchanged) -- this is what the single "flip" button already did. flipVertical is its
        // sibling, mirroring across the horizontal axis instead (#569).
        let transformed: { x: number; y: number };
        if (kind === 'flipHorizontal') transformed = { x: cx - dx, y: point.y };
        else if (kind === 'flipVertical') transformed = { x: point.x, y: cy - dy };
        else if (kind === 'left') transformed = { x: pivot.x + dyPivot, y: pivot.y - dxPivot };
        else transformed = { x: pivot.x - dyPivot, y: pivot.y + dxPivot };
        return { id: point.id, ...transformed };
      });
      const offsetX =
        Math.max(0, -Math.min(...transformedPoints.map((point) => point.x))) +
        Math.min(0, paperSize.width - Math.max(...transformedPoints.map((point) => point.x)));
      const offsetY =
        Math.max(0, -Math.min(...transformedPoints.map((point) => point.y))) +
        Math.min(0, paperSize.height - Math.max(...transformedPoints.map((point) => point.y)));
      const positions = Object.fromEntries(
        transformedPoints.map((point) => [
          point.id,
          { x: (point.x + offsetX) / paperSize.width, y: (point.y + offsetY) / paperSize.height }
        ])
      );
      const flipped = new Set(selected.map((atom) => atom.id));
      moveSelectedAtoms(
        positions,
        kind === 'flipHorizontal' || kind === 'flipVertical' ? (moved) => swapWedgesFrom(moved, flipped) : undefined
      );
    },
    [atoms, moveSelectedAtoms, selectedTransformAtomIds, rotationPivot, paperSize]
  );

  const rotateSelection3d = useCallback(
    (axis: RotationAxis, degrees: number) => {
      commitDocument((current) => {
        const size = current.depiction.coordinateSet;
        const normalizedPivot = rotationPivot
          ? unprojectPointPreservingAspect(rotationPivot, size, paperSize, !isOversizedImport, false)
          : undefined;
        return rotateStructures3d(
          current,
          selection,
          axis,
          degrees,
          normalizedPivot ? { x: normalizedPivot.x * size.width, y: normalizedPivot.y * size.height } : undefined
        );
      });
    },
    [commitDocument, selection, rotationPivot, paperSize, isOversizedImport]
  );

  // #568 (corrected scope, per user feedback with reference screenshots after the first cut):
  // align/distribute operate on whole *objects* -- a connected molecule fragment, a free-floating
  // reaction arrow, or a shape -- moved as rigid units, not on individual atom coordinates. See
  // objectAlignment.ts's own doc comment for exactly what counts as an alignable object and why.
  // #855 -- relative to the page (the grown page sheet) or the other objects, and Distribute
  // with an optional fixed gap in drawing units.
  const alignSelection = useCallback(
    (kind: AlignKind, options: { readonly relativeTo?: 'objects' | 'page'; readonly spacing?: number } = {}) => {
      commitDocument((current) =>
        alignInReaction(current, selection, kind, {
          ...options,
          ...(options.relativeTo === 'page' ? { page: pageRect(pageOf(current)) } : {})
        })
      );
    },
    [alignInReaction, commitDocument, selection]
  );

  // #855 -- To Front / Forward / Backward / To Back for the selected annotations (one undo step).
  const reorderSelection = useCallback(
    (command: StackingCommand) => {
      const ids = selection.annotationIds ?? [];
      if (ids.length === 0) return;
      commitDocument((current) => reorderAnnotations(current, ids, command));
    },
    [commitDocument, selection]
  );

  const callouts = calloutSelection(document, selection);
  const attachSelectedCallouts = useCallback(() => {
    if (!callouts.target) return;
    commitDocument((current) => setCalloutAttachment(current, callouts.ids, callouts.target));
  }, [commitDocument, callouts.ids, callouts.target]);
  const detachSelectedCallouts = useCallback(() => {
    commitDocument((current) => setCalloutAttachment(current, callouts.ids));
  }, [commitDocument, callouts.ids]);

  const deleteSelection = useCallback(() => {
    if (!hasSelection(selection)) return;
    commitDocument((current) =>
      selection.atomIds.reduce(
        (next, id) => deleteAtom(next, id),
        selection.bondIds.reduce<DrawingDocument>((next, id) => deleteBond(next, id), {
          ...current,
          annotations: current.annotations.filter((annotation) => !selection.annotationIds?.includes(annotation.id))
        })
      )
    );
    setSelection(emptySelection());
  }, [commitDocument, selection, setSelection]);

  // Internal editable clipboard remains available even if the system image write is blocked.
  // #756 -- or, for a text-only selection, copies of the text annotations (textClipboard.ts).
  const [clipboard, setClipboard] = useState<
    | { readonly kind: 'fragment'; readonly fragment: ClipboardFragment }
    | { readonly kind: 'text'; readonly texts: readonly TextAnnotation[] }
    | null
  >(null);
  const pastePosition = useRef<NormalizedPoint>({ x: 0.5, y: 0.5 });
  const updatePastePosition = useCallback((point: NormalizedPoint) => {
    pastePosition.current = point;
  }, []);

  // Copyable: atoms without annotations (a structure fragment), or text and nothing else. Mixed
  // selections stay guarded until #638's faithful mixed clipboard.
  const canCopySelection =
    (selection.atomIds.length > 0 && !selection.annotationIds?.length) ||
    textOnlySelection(document, selection) !== null;

  const copySelection = useCallback(() => {
    const texts = textOnlySelection(document, selection);
    if (texts) {
      setClipboard({ kind: 'text', texts: structuredClone(texts) });
      onCopySelection?.();
      return;
    }
    if (!selection.atomIds.length || selection.annotationIds?.length) return;
    const fragment = copyFragment(document, selection.atomIds);
    if (!fragment) return;
    setClipboard({ kind: 'fragment', fragment });
    onCopySelection?.();
  }, [document, selection, onCopySelection]);

  const cutSelection = useCallback(() => {
    if (!canCopySelection) return;
    copySelection();
    deleteSelection();
  }, [canCopySelection, copySelection, deleteSelection]);

  const pasteClipboardAt = useCallback(
    (point: NormalizedPoint) => {
      if (!clipboard) return;
      const target = unprojectPointPreservingAspect(
        point,
        document.depiction.coordinateSet,
        paperSize,
        !isOversizedImport,
        false
      );
      if (clipboard.kind === 'text') {
        const source = document.depiction.coordinateSet;
        const { document: pasted, pastedIds } = pasteTexts(document, clipboard.texts, {
          x: target.x * source.width,
          y: target.y * source.height
        });
        commitDocument(() => pasted);
        setSelection({ atomIds: [], bondIds: [], annotationIds: pastedIds });
        return;
      }
      const { document: pasted, pastedAtomIds, pastedBondIds } = pasteFragment(document, clipboard.fragment, target);
      commitDocument(() => pasted);
      setSelection({ atomIds: pastedAtomIds, bondIds: pastedBondIds });
    },
    [clipboard, commitDocument, document, paperSize, isOversizedImport, setSelection]
  );

  const pasteClipboard = useCallback(() => pasteClipboardAt(pastePosition.current), [pasteClipboardAt]);

  // #698 -- the reaction arrow's type can be reassigned at any time: one undo step, the arrow edited
  // in place, so its containers stay. (Reversing it lives with the boxes: useReactionBoxes.)
  const setReactionArrowKind = useCallback(
    (arrowId: string, arrowKind: ReactionArrowKind, overlay?: ReactionArrowOverlay) => {
      commitDocument((current) => ({
        ...current,
        annotations: current.annotations.map((annotation) => {
          if (annotation.id !== arrowId || annotation.kind !== 'reaction-arrow') return annotation;
          const { overlay: _previous, ...rest } = annotation;
          return { ...rest, arrowKind, ...(overlay ? { overlay } : {}) };
        })
      }));
    },
    [commitDocument]
  );

  /** #689 -- "Add to paper": `drawn` (a depicted molecule or isomer) joins the drawing as a new,
   *  disconnected fragment beside what is already there -- one undo step, left selected so it
   *  can be dragged into place. Stereo travels with it (see clipboard.ts).
   *  #699/#700 -- `target` puts it elsewhere (see AdditionTarget); `commit` replaces the plain
   *  commit (adding into a reaction box grows the box in the same step). */
  const addDrawingAlongside = useCallback(
    (
      drawn: DrawingDocument,
      where?: AdditionTarget,
      commit: (next: DrawingDocument, atomIds: readonly string[]) => void = (next) => commitDocument(() => next)
    ) => {
      const fragment = copyFragment(
        drawn,
        drawn.structure.graph.nodes.map((node) => node.id)
      );
      if (!fragment) return;
      const { coordinateSet } = document.depiction;
      let target: NormalizedPoint;
      if (!where) target = placeAlongside(document, fragment);
      else if ('paper' in where) {
        target = unprojectPointPreservingAspect(where.paper, coordinateSet, paperSize, !isOversizedImport, false);
      } else {
        const points = Object.values(fragment.positions);
        const span = (values: readonly number[]) => Math.max(...values) - Math.min(...values);
        const centre = where.place({
          width: span(points.map((point) => point.x)) * (fragment.size?.width ?? coordinateSet.width),
          height: span(points.map((point) => point.y)) * (fragment.size?.height ?? coordinateSet.height)
        });
        target = { x: centre.x / coordinateSet.width, y: centre.y / coordinateSet.height };
      }
      const { document: pasted, pastedAtomIds, pastedBondIds } = pasteFragment(document, fragment, target);
      // #707 -- it keeps its own size, so a long chain can outgrow the paper: the drawing grows to
      // hold it, and a drawing bigger than the paper is shown shrunk to fit (as an import is).
      const added = containPastedAtoms(pasted, pastedAtomIds, ADD_TO_PAPER_MARGIN);
      const { width, height } = added.depiction.coordinateSet;
      commit(added, pastedAtomIds);
      if (width > paperSize.width || height > paperSize.height) setIsOversizedImport(true);
      setSelection({ atomIds: pastedAtomIds, bondIds: pastedBondIds });
    },
    [commitDocument, document, isOversizedImport, paperSize, setIsOversizedImport, setSelection]
  );

  // #772 -- page setup: one undoable step per change; content positions are never touched
  // (accepted: orientation changes and resizing preserve artwork positions and sizes).
  const setPage = useCallback(
    (page: PageSetup) => {
      commitDocument((current) =>
        JSON.stringify(current.page) === JSON.stringify(page) ? current : { ...current, page }
      );
    },
    [commitDocument]
  );

  const clearDrawing = useCallback(() => {
    commitDocument(() => createEmptyDrawingDocument(paperSize));
    setSelection(emptySelection());
    setIsOversizedImport(false);
  }, [commitDocument, setIsOversizedImport, setSelection, paperSize]);

  const updateSelectedAtom = useCallback(
    (atomicNumber: number) => {
      if (!singleAtomId) return;
      const nodeId = singleAtomId;
      commitDocument((current) => setAtomAtomicNumber(current, nodeId, atomicNumber));
    },
    [commitDocument, singleAtomId]
  );

  // The periodic-table tool (#255): repaint any atom by clicking it, no selection step.
  const changeAtomElement = useCallback(
    (atomId: string, atomicNumber: number, formalCharge?: number) => {
      commitDocument((current) => {
        const retyped = setAtomAtomicNumber(current, atomId, atomicNumber);
        return formalCharge === undefined ? retyped : setAtomFormalCharge(retyped, atomId, formalCharge);
      });
    },
    [commitDocument]
  );

  // UI action rules Rule 4.4: with an element armed, a click on empty drawingPaper places a lone
  // atom of that element -- a disconnected node, i.e. a new molecule. `point` is paper-normalized;
  // unproject it into the document's coordinate space the same way freehand bonds do.
  const placeAtom = useCallback(
    (point: Readonly<{ x: number; y: number }>, atomicNumber: number, formalCharge?: number) => {
      commitDocument((current) => {
        const placed = addAtom(
          current,
          unprojectPointPreservingAspect(
            point,
            { width: current.depiction.coordinateSet.width, height: current.depiction.coordinateSet.height },
            paperSize,
            !isOversizedImport
          ),
          atomicNumber
        );
        const added = placed.structure.graph.nodes.find((node) => !current.structure.atoms[node.id]);
        return formalCharge === undefined || !added ? placed : setAtomFormalCharge(placed, added.id, formalCharge);
      });
    },
    [commitDocument, isOversizedImport, paperSize]
  );

  // UI action rules Rule 3: clicking on / near an existing bond's midpoint with a bond tool
  // armed applies that tool to the bond. Each domain setter already no-ops on a bond it can't
  // act on (a wedge needs order 1, crossed needs order 2), so the guards here stay thin.
  const applyArmedBondTool = useCallback(
    (bondId: string) => {
      commitDocument((current) => {
        switch (armedBondTool.kind) {
          case 'order': {
            // #453: setBondOrder no-ops (same document, no history entry) when the bond is
            // already that order -- for the double-bond tool on a fusion bond, that's exactly
            // when clicking should instead cycle which of its two bordering rings it faces.
            const reordered = setBondOrder(current, bondId, armedBondTool.order);
            return armedBondTool.order === 2 && reordered === current
              ? cycleBondDoubleSide(current, bondId)
              : reordered;
          }
          case 'wedge':
            return setBondWedge(current, bondId, armedBondTool.style);
          case 'crossed-double':
            return setBondCrossedDouble(current, bondId, true);
          case 'dative': {
            const edge = current.structure.graph.edges.find((candidate) => candidate.id === bondId);
            return edge ? setBondDative(current, bondId, edge.source) : current;
          }
          case 'ionic':
            return ionizeBond(current, bondId);
          case 'hydrogen':
            return setBondHydrogen(current, bondId);
          case 'agostic':
            return setBondAgostic(current, bondId);
          case 'wavy':
            return setBondWavy(current, bondId);
          case 'reverse':
            return current.structure.bonds[bondId]?.type === 'dative'
              ? reverseBondDative(current, bondId)
              : stereoBonds[bondId]?.type === 'wavy'
                ? reverseBondWavy(current, bondId)
                : reverseBondWedge(current, bondId);
          default:
            return current;
        }
      });
    },
    [armedBondTool, commitDocument, stereoBonds]
  );

  const applyAtomFormalCharge = useCallback(
    (atomId: string, formalCharge: number) => {
      commitDocument((current) => {
        const charge = current.structure.formalCharges[atomId];
        return charge === undefined ? current : setAtomFormalCharge(current, atomId, formalCharge);
      });
    },
    [commitDocument]
  );

  const updateSelectedFormalCharge = useCallback(
    (formalCharge: number) => {
      if (!singleAtomId || !Number.isInteger(formalCharge)) return;
      const nodeId = singleAtomId;
      commitDocument((current) => setAtomFormalCharge(current, nodeId, formalCharge));
    },
    [commitDocument, singleAtomId]
  );

  const updateSelectedResonanceCharge = useCallback(
    (resonanceCharge: number | null) => {
      if (!singleAtomId) return;
      const nodeId = singleAtomId;
      commitDocument((current) => setAtomResonanceCharge(current, nodeId, resonanceCharge));
    },
    [commitDocument, singleAtomId]
  );

  const updateSelectedIsotope = useCallback(
    (isotope: number | null) => {
      if (!singleAtomId) return;
      const nodeId = singleAtomId;
      commitDocument((current) => setAtomIsotope(current, nodeId, isotope));
    },
    [commitDocument, singleAtomId]
  );

  const updateSelectedRadicalElectronCount = useCallback(
    (radicalElectronCount: number | null) => {
      if (!singleAtomId) return;
      const nodeId = singleAtomId;
      commitDocument((current) => setAtomRadicalElectronCount(current, nodeId, radicalElectronCount));
    },
    [commitDocument, singleAtomId]
  );

  return {
    placeFunctionalGroup,
    groupError,
    groupsContracted,
    setGroupsContracted,
    showHydrogens,
    hideHydrogens,
    hydrogensShown,
    setHydrogensShown,
    cleanUpSelection,
    isCleaningUp,
    cleanupError,
    setPage,
    moveSelection,
    moveSelectedAnnotations,
    updateImplicitHydrogenDirection,
    moveSelectedAtoms,
    transformSelection,
    rotateSelection3d,
    alignSelection,
    reorderSelection,
    canAttachCallouts: callouts.ids.length > 0 && !!callouts.target,
    canDetachCallouts: callouts.canDetach,
    attachSelectedCallouts,
    detachSelectedCallouts,
    deleteSelection,
    clearDrawing,
    updateSelectedAtom,
    changeAtomElement,
    placeAtom,
    applyArmedBondTool,
    updateSelectedFormalCharge,
    applyAtomFormalCharge,
    updateSelectedResonanceCharge,
    updateSelectedIsotope,
    updateSelectedRadicalElectronCount,
    copySelection,
    cutSelection,
    pasteClipboard,
    pasteClipboardAt,
    addDrawingAlongside,
    setReactionArrowKind,
    updatePastePosition,
    hasClipboard: clipboard !== null,
    canCopySelection
  };
}
